import React, { useCallback, useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { reduce } from 'lodash';
import parse from 'html-react-parser';
import sanitize from 'sanitize-html';
import { getTransformationUrlV2 } from '@com/core/common/utils/image';
import { subscriptionsSVC } from '@com/core/common/services/api';
import { AppContext } from '@com/core/common/contexts/appContext';
import { useTranslation } from '@com/core/common/translations/i18n';
import { breakpoints } from '@com/core/common/styles/baseStyles';
import { TProduct, THit } from '@com/core/common/types/catalog';
import Link from '@com/core/common/components/Link';
import { CDN_IMG_BASE } from '@com/core/common/baseConfig';
import BlockButton from '../../common/BlockButton';
import Loader from '../../common/Loader';
import SubscribeContent from './SubscribeContent';
import PaymentSelector from '../../cart/PaymentSelector';

const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const staticDays = [
    { name: 'S', id: 6 },
    { name: 'M', id: 0 },
    { name: 'T', id: 1 },
    { name: 'W', id: 2 },
    { name: 'T', id: 3 },
    { name: 'F', id: 4 },
    { name: 'S', id: 5 },
];

type TProps = {
    product: TProduct | THit;
    skuConfig: string;
    offerCode: string;
    showSubscriptionBox?: boolean;
    subscriptionModalView?: string;
    closeModal?: () => void;
};

type TPaymentMethod = {
    message: string;
    selectedPaymentMethod: string | null;
    validPaymentMethods: {
        code: string;
        fee: string | null;
        isSubscribable: boolean;
    }[];
};

const Subscription = ({
    closeModal,
    offerCode,
    product,
    showSubscriptionBox = true,
    skuConfig,
    subscriptionModalView = '',
}: TProps): JSX.Element | null => {
    const { t } = useTranslation('common');
    const { locale } = useContext(AppContext);
    const { subscription } = product;

    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [modalView, setModalView] = useState(subscriptionModalView);
    const [scheduleData, setScheduleData] = useState(null);
    const [scheduleQty, setScheduleQty] = useState(null);
    const [totalScheduledQty, setTotalScheduledQty] = useState<number>(0);
    const [subscribeData, setSubscribeData] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successType, setSuccessType] = useState<string>('');
    const [subscriptionPaymentMethod, setSubscriptionPaymentMethod] = useState<TPaymentMethod>();
    const [showPaymentSelectorModal, setShowPaymentSelectorModal] = useState(false);

    const getData = useCallback(async () => {
        setIsDataLoading(true);
        try {
            const { data } = await subscriptionsSVC(locale.apiLocale).getSubscriptionData(skuConfig);
            setIsDataLoading(false);
            setScheduleData(data.data.subWeek);
            const currentSchedule = reduce(
                data.data.subWeek,
                (result, currentValue) => ({ ...result, [currentValue.deliveryDate]: currentValue.quantity }),
                {},
            );
            const currentTotalScheduledQty = reduce(
                data.data.subWeek,
                (result, currentValue) => result + currentValue.quantity,
                0,
            );
            setScheduleQty(currentSchedule);
            setTotalScheduledQty(currentTotalScheduledQty);
            setSubscribeData(data.data.subProduct);
            setSubscriptionPaymentMethod(data.data.paymentMethod);
        } catch (err) {
            console.log(err);
            setIsDataLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skuConfig]);

    useEffect(() => {
        if (modalView || showSuccessModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [modalView, showSuccessModal]);

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skuConfig]);

    // opening schedule or subscribe for the first time e.g. not switching tabs
    const openActionModal = (action: 'schedule' | 'subscribe') => {
        getData();
        setActionError('');
        setActionSuccess('');
        setModalView(action);
    };

    // schedule stuff
    const updateScheduleQty = (date: string, step: number) => {
        const currentQuantity = scheduleQty[date];
        let newQuantity = currentQuantity + step;
        if (step === 0) {
            newQuantity = currentQuantity > 0 ? 0 : 1;
        }
        setScheduleQty({ ...scheduleQty, [date]: newQuantity });
    };

    const canScheduleSubmit = () => {
        const quantitiesSum = reduce(scheduleQty, (sum, quantity) => sum + quantity, 0);
        if (quantitiesSum === 0 && !totalScheduledQty) {
            return true;
        }

        return false;
    };

    const submitSchedule = async (): Promise<void> => {
        setIsActionLoading(true);
        try {
            await subscriptionsSVC(locale.apiLocale).createSchedule(skuConfig, {
                weekSchedule: scheduleQty,
            });
            setIsActionLoading(false);
            setSuccessType('schedule');
            setShowSuccessModal(true);
            setModalView('');
            getData();
        } catch (err) {
            setActionError(err || 'Unable to schedule this item. Please try again later.');
            setIsActionLoading(false);
        }
    };

    if (!subscription.is_subscribable && !subscription.is_schedulable) return null;

    const renderSubscriptionBox = () => {
        if (totalScheduledQty) {
            return (
                <SubscriptionRow>
                    <SubscriptionChart>
                        <div className="subscriptionStage">
                            <img src={`${CDN_IMG_BASE}/creativeIcons/subscription-stage-active.svg`} alt="" />
                            <span className="value">{subscription.sub_discount.stages[0]}%</span>
                        </div>
                        {subscription.sub_discount.stages.length > 1 ? (
                            <>
                                <img src={`${CDN_IMG_BASE}/simpleIcons/chevron-right.svg`} alt="" className="arrow" />
                                <div className="subscriptionStage">
                                    <img src={`${CDN_IMG_BASE}/creativeIcons/subscription-stage-inactive.svg`} alt="" />
                                    <span className="value">{subscription.sub_discount.stages[1]}%</span>
                                </div>
                            </>
                        ) : null}
                    </SubscriptionChart>
                    <CalendarWeek>
                        {scheduleData.map(day => {
                            const d = new Date(day.deliveryDate);
                            const dayLabel = dayLabels[d.getDay()];
                            const dateInMonth = d.getDate();

                            return (
                                <div key={day.deliveryDate} className="dayContainer">
                                    <span className="label">{dayLabel}</span>
                                    <button
                                        type="button"
                                        // onClick={() => setSelectedDate(day)}
                                        className={day.quantity > 0 ? 'active' : ''}
                                        disabled={day.quantity > 0}>
                                        {dateInMonth}
                                        {!!day.quantity && <span className="count">{day.quantity}</span>}
                                    </button>
                                </div>
                            );
                        })}
                    </CalendarWeek>
                    <ButtonContainer>
                        <ManageTrigger type="button" onClick={() => openActionModal('schedule')}>
                            {t('manage')}
                        </ManageTrigger>
                    </ButtonContainer>
                </SubscriptionRow>
            );
        }
        // if (subscribeData) {
        //     return 'subscription';
        // }
        if (!subscription.is_subscribable && subscription.is_schedulable) {
            // schedule only
            return (
                <SubscriptionTrigger type="button" onClick={() => openActionModal('schedule')}>
                    <img src={`${CDN_IMG_BASE}/simpleIcons/calendar-brandPrimary.svg`} alt="" />
                    {t('schedule')}
                </SubscriptionTrigger>
            );
        }
        return (
            // SUBSCRIBE OR SCHEDULE
            <SubscriptionRow>
                <SubscriptionChart>
                    <div className="subscriptionStage">
                        <img src={`${CDN_IMG_BASE}/creativeIcons/subscription-stage-active.svg`} alt="" />
                        <span className="value">{subscription.sub_discount.stages[0]}%</span>
                    </div>
                    {subscription.sub_discount.stages.length > 1 ? (
                        <>
                            <img src={`${CDN_IMG_BASE}/simpleIcons/chevron-right.svg`} alt="" className="arrow" />
                            <div className="subscriptionStage">
                                <img src={`${CDN_IMG_BASE}/creativeIcons/subscription-stage-inactive.svg`} alt="" />
                                <span className="value">{subscription.sub_discount.stages[1]}%</span>
                            </div>
                        </>
                    ) : null}
                </SubscriptionChart>
                <div className="info">
                    <p>
                        {`${t('subscribe-to-save')} ${subscription.sub_discount.stages[0]}%`}
                        {subscription.sub_discount.stages.length > 1
                            ? ` - ${subscription.sub_discount.stages[1]}
                        %`
                            : ''}
                    </p>
                    <button type="button" onClick={() => setModalView('info')}>
                        {t('learn-more')}
                        <img src={`${CDN_IMG_BASE}/simpleIcons/info-bodyLight.svg`} alt="" />
                    </button>
                </div>
                <SubscriptionTrigger type="button" onClick={() => openActionModal('subscribe')}>
                    <img src={`${CDN_IMG_BASE}/simpleIcons/calendar-brandPrimary.svg`} alt="" />
                    {t('subscribe')}
                </SubscriptionTrigger>
            </SubscriptionRow>
        );
    };

    const renderSuccessModal = () => {
        const type = successType;
        switch (type) {
            case 'subscription':
                return (
                    <>
                        <div className="header">{t('subscription_added')}</div>
                        {subscriptionPaymentMethod &&
                            !subscriptionPaymentMethod.selectedPaymentMethod &&
                            subscriptionPaymentMethod.message && (
                                <PaymentError>
                                    <img src={`${CDN_IMG_BASE}/simpleIcons/alert-triangle.svg`} alt="warning" />
                                    <p>{subscriptionPaymentMethod.message}</p>
                                </PaymentError>
                            )}
                        <div className="contentCtr">
                            <img
                                width={80}
                                src={getTransformationUrlV2(
                                    product ? product.image_key : subscribeData.imageKey,
                                    '200',
                                )}
                                alt="productimage"
                            />

                            <div className="mainContent">
                                <p className="title">{product ? product.name : subscribeData.title}</p>

                                <p className="selectedQty">
                                    {t('quantity')} {subscribeData.quantity}
                                </p>
                                <p className="selectedPeriod">
                                    <span>
                                        <img
                                            src={`${CDN_IMG_BASE}/simpleIcons/repeat-brandPrimary.svg`}
                                            alt="recurring"
                                        />
                                    </span>
                                    {`${
                                        subscribeData.subType === 'monthly'
                                            ? t('subscription_month', { count: subscribeData.subData.nt })
                                            : t('subscription_week', { count: subscribeData.subData.nt })
                                    }`}
                                </p>
                                {subscribeData.subType === 'monthly' ? (
                                    <div className="dates">
                                        {subscribeData.subData.dt.map((date: number, i: number) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <div className="selectedDate" key={i}>
                                                {`${date}${date === 1 ? 'st' : 'th'}`}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="dayCtr">
                                        {staticDays.map((day, ind) => (
                                            <div
                                                className={`dayContent ${
                                                    subscribeData.subData.dy?.includes(day.id) ? 'activeDay' : ''
                                                }`}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={ind}>
                                                {day.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="bottom">
                                    <p>{parse(subscribeData?.deliveryMessage || '')}</p>
                                </div>
                            </div>
                        </div>
                    </>
                );
            case 'schedule':
                return <p>{t('one_time_schedule_success')}</p>;
            default:
                return null;
        }
    };

    const handleSubscriptionSuccess = (isSuccess: boolean) => {
        if (isSuccess) {
            getData();
            setModalView('');
            setShowSuccessModal(true);
            setSuccessType('subscription');
        }
    };
    const closeSuccessModal = () => {
        setShowSuccessModal(false);
        setSuccessType('');
        if (closeModal) {
            closeModal();
        }
    };

    return (
        <>
            {showSubscriptionBox && renderSubscriptionBox()}

            {modalView && (
                <Modal>
                    <div
                        className="scrim"
                        onClick={() => {
                            setModalView('');
                            if (closeModal) {
                                closeModal();
                            }
                        }}
                        aria-hidden="true"
                    />

                    {/* --------------------------- INFO MODAL START */}
                    {modalView === 'info' && (
                        <InfoContent className="content">
                            <h3>{t('subscription-savings')}</h3>
                            <img src={`${CDN_IMG_BASE}/images/subscription-info-hero.png`} alt="" />
                            <p className="title">{subscription.sub_discount.title}</p>
                            <p className="desc">
                                {parse(
                                    sanitize(subscription.sub_discount.message, {
                                        allowedAttributes: false,
                                    }),
                                )}
                            </p>
                            <div className="actionRow">
                                <Link href="/auto-delivery" external>
                                    {t('learn-more')}
                                </Link>
                                <span>{t('or')}</span>
                                <button type="button" onClick={() => openActionModal('subscribe')}>
                                    {t('start-saving')}
                                </button>
                            </div>
                        </InfoContent>
                    )}
                    {/* --------------------------- INFO MODAL END */}

                    {(modalView === 'subscribe' || modalView === 'schedule') && (
                        <SubscriptionContent className="content">
                            {/* ---------------------------  TABS START */}
                            <div className="tabOptions">
                                {subscription.is_subscribable && subscribeData && (
                                    <button
                                        type="button"
                                        onClick={() => setModalView('subscribe')}
                                        className={modalView === 'subscribe' ? 'active' : ''}
                                        disabled={modalView === 'subscribe'}>
                                        {t('subscription-recurring')}{' '}
                                        <span className="savingsTag">
                                            {`${t('save')} ${
                                                subscription.sub_discount.stages.length > 1
                                                    ? subscription.sub_discount.stages[1]
                                                    : subscription.sub_discount.stages[0]
                                            }%`}
                                        </span>
                                    </button>
                                )}
                                {subscription.is_schedulable && scheduleData && (
                                    <button
                                        type="button"
                                        onClick={() => setModalView('schedule')}
                                        className={modalView === 'schedule' ? 'active' : ''}
                                        disabled={modalView === 'schedule'}>
                                        {t('one-time-schedule')}
                                    </button>
                                )}
                            </div>
                            {/* ---------------------------  TABS END */}
                            {isDataLoading ? (
                                <div className="loaderContainer">
                                    <Loader color="brand" dotSize={16} />
                                </div>
                            ) : (
                                <>
                                    {/* ---------------------------  SUBSCRIBE SECTION START */}
                                    {modalView === 'subscribe' && (
                                        <SubscribeContent
                                            offerCode={offerCode}
                                            subscribeData={subscribeData}
                                            sku={skuConfig}
                                            onSubscriptionSuccess={handleSubscriptionSuccess}
                                        />
                                    )}
                                    {/* ---------------------------  SUBSCRIBE SECTION END */}

                                    {/* ---------------------------  SCHEDULE SECTION START */}
                                    {modalView === 'schedule' && scheduleData && (
                                        <ScheduleSection>
                                            {/* cli todo add cancel/close buttons? */}
                                            {actionError && <p>{actionError}</p>}
                                            {actionSuccess && <p>{actionSuccess}</p>}
                                            {!actionError && !actionSuccess && (
                                                <>
                                                    <p className="title">{t('set_quantity')}</p>
                                                    <div className="scheduleOptions">
                                                        {scheduleQty &&
                                                            scheduleData.map(day => {
                                                                const dateInMonth = new Date(
                                                                    day.deliveryDate,
                                                                ).getDate();
                                                                const dayQty = scheduleQty[day.deliveryDate];
                                                                return (
                                                                    <ScheduleOption key={day.deliveryDate}>
                                                                        <button
                                                                            className={`date${
                                                                                dayQty > 0 ? ' active' : ''
                                                                            }`}
                                                                            type="button"
                                                                            onClick={() =>
                                                                                updateScheduleQty(day.deliveryDate, 0)
                                                                            }>
                                                                            <span> {day.deliveryDateParam} </span>
                                                                            <strong>{dateInMonth}</strong>
                                                                        </button>
                                                                        <div className="qtyChanger">
                                                                            <button
                                                                                type="button"
                                                                                className="addition"
                                                                                onClick={() =>
                                                                                    updateScheduleQty(
                                                                                        day.deliveryDate,
                                                                                        1,
                                                                                    )
                                                                                }
                                                                                disabled={dayQty === day.quantityMax}>
                                                                                <span>
                                                                                    <img
                                                                                        src={`${CDN_IMG_BASE}/simpleIcons/plus-${
                                                                                            dayQty === day.quantityMax
                                                                                                ? 'bodyLight'
                                                                                                : 'brandPrimary'
                                                                                        }.svg`}
                                                                                        alt=""
                                                                                    />
                                                                                </span>
                                                                            </button>
                                                                            <span
                                                                                className={`qty${
                                                                                    dayQty > 0 ? ' active' : ''
                                                                                }`}>
                                                                                {dayQty}
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                className={`removal ${
                                                                                    dayQty > 0 ? 'active' : ''
                                                                                }`}
                                                                                onClick={() =>
                                                                                    updateScheduleQty(
                                                                                        day.deliveryDate,
                                                                                        -1,
                                                                                    )
                                                                                }
                                                                                disabled={dayQty < 1}>
                                                                                <span>
                                                                                    <img
                                                                                        src={`${CDN_IMG_BASE}/simpleIcons/minus-${
                                                                                            dayQty < 1
                                                                                                ? 'bodyLight'
                                                                                                : 'brandPrimary'
                                                                                        }.svg`}
                                                                                        alt=""
                                                                                    />
                                                                                </span>
                                                                            </button>
                                                                        </div>
                                                                    </ScheduleOption>
                                                                );
                                                            })}
                                                    </div>
                                                    <p className="helper">
                                                        *{t('will-receive-auto-deliveries-message')}
                                                    </p>
                                                    {subscription.is_subscribable && (
                                                        <div className="subscribeEnticer">
                                                            <div>
                                                                <p className="title">{t('want_to_save_more')}?</p>
                                                                <p>
                                                                    {t('choose')}{' '}
                                                                    <span
                                                                        onClick={() => setModalView('subscribe')}
                                                                        aria-hidden="true">
                                                                        {t('recurring-subscription')}
                                                                    </span>
                                                                    , {t('select-subscription-start-saving-money')}!
                                                                </p>
                                                            </div>
                                                            <img
                                                                src={`${CDN_IMG_BASE}/creativeIcons/subscriptions-head.svg`}
                                                                alt="Subscriptions-Head"
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="ctaContainer">
                                                        <BlockButton
                                                            pillStyle
                                                            onClick={submitSchedule}
                                                            disabled={canScheduleSubmit()}>
                                                            {isActionLoading ? <Loader /> : t('confirm')}
                                                        </BlockButton>
                                                    </div>
                                                </>
                                            )}
                                        </ScheduleSection>
                                    )}
                                    {/* ---------------------------  SCHEDULE SECTION END */}
                                </>
                            )}
                        </SubscriptionContent>
                    )}
                </Modal>
            )}
            {showSuccessModal && (
                <Modal>
                    <div
                        className="scrim"
                        onClick={() => {
                            closeSuccessModal();
                        }}
                        aria-hidden="true"
                    />
                    <SuccessModal>
                        {isDataLoading ? (
                            <div className="loaderContainer">
                                <Loader color="brand" dotSize={16} />
                            </div>
                        ) : (
                            <>
                                <img src={`${CDN_IMG_BASE}/simpleIcons/check-circle-brandPrimary.svg`} alt="check" />

                                {renderSuccessModal()}
                                <div className="divider" />
                                {!subscriptionPaymentMethod.selectedPaymentMethod && successType === 'subscription' ? (
                                    <PaymentButtons>
                                        <button
                                            onClick={() => setShowPaymentSelectorModal(true)}
                                            className="changePayment"
                                            type="button">
                                            <p>{t('change_payment').toLocaleUpperCase()}</p>
                                        </button>
                                        <button type="button" onClick={() => closeSuccessModal()}>
                                            <p>{t('cancel').toLocaleUpperCase()}</p>
                                        </button>
                                    </PaymentButtons>
                                ) : (
                                    <DoneButton onClick={() => closeSuccessModal()}>
                                        {t('done').toLocaleUpperCase()}
                                    </DoneButton>
                                )}
                            </>
                        )}
                    </SuccessModal>
                </Modal>
            )}

            {showPaymentSelectorModal && (
                <PaymentSelector
                    closeModal={() => {
                        closeSuccessModal();
                        setShowPaymentSelectorModal(false);
                    }}
                />
            )}
        </>
    );
};

const DoneButton = styled.button`
    display: flex;
    margin: 10px auto;
    color: ${({ theme }): string => theme.colors.brandPrimary};
    font-size: 0.75rem;
    font-weight: 600;
`;

const PaymentButtons = styled.div`
    display: flex;
    > button {
        display: flex;
        padding: 15px 0;
        color: ${({ theme }): string => theme.colors.bodyLight};
        font-size: 0.75rem;
        flex: 1;
        font-weight: 600;
        &:first-child {
            border-right: 1px solid #f0f1f5;
        }
    }
    p {
        text-align: center;
        width: 100%;
    }
    .changePayment {
        color: ${({ theme }): string => theme.colors.brandPrimary};
    }
`;

const PaymentError = styled.div`
    display: flex;
    margin: 15px;
    padding: 15px;
    background: rgba(250, 0, 0, 0.06);
    border-radius: 3px;
    color: #fa0000;
    > p {
        font-weight: 600;
        ${({ theme: { left } }): string => `margin-${left}: 10px`};
    }
`;

const SuccessModal = styled.div`
    background: ${({ theme }): string => theme.colors.bodyAlt};
    border-radius: 5px;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    z-index: 3;
    min-width: 25vw;
    min-height: 120px;

    .header {
        text-align: center;
        padding: 15px 0 25px;
        font-size: 1.125rem;
        font-weight: 600;
        border-bottom: 1px solid #f0f1f5;
    }

    > img {
        margin: 30px auto 0;
        width: 36px;
    }
    > p {
        margin: 10px;
        font-weight: 600;
        font-size: 0.875rem;
        text-align: center;
    }
    .divider {
        border-bottom: 1px solid #f0f1f5;
        width: 100%;
    }

    .dayCtr {
        display: flex;
        padding: 10px 0;
    }
    .dayContent {
        width: 22px;
        height: 22px;
        color: ${({ theme }): string => theme.colors.border};
        display: flex;
        justify-content: center;
        align-items: center;
        border: #f0f1f5;
        border-radius: 100%;
        margin: 3px;
        font-size: 0.6175rem;
        font-weight: 600;
        background-color: #f0f1f5;
        padding-top: 2px;
    }
    .activeDay {
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
        border: 1px solid #f0f1f5;
        box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.04);
        color: ${({ theme }): string => theme.colors.bodyMid};
    }
    .mainContent {
        font-size: 0.75rem;
        padding-left: 10px;
        flex: 1;

        > div {
            display: flex;
        }
        .title {
            font-weight: 600;
            font-size: 0.875rem;
            margin-bottom: 2px;
        }
        .selectedQty {
            color: ${({ theme }): string => theme.colors.bodyLight};
            margin-bottom: 5px;
            font-size: 0.6875rem;
        }
        .selectedPeriod {
            font-size: 0.75rem;
            width: fit-content;
            display: flex;
            align-items: center;
            img {
                width: 12px;
                height: 12px;
                ${({ theme: { right } }): string => `margin-${right}: 5px`};
            }
        }
        .selectedDate {
            padding: 1px 5px;
            border: 1px solid ${({ theme }): string => theme.colors.border};
            width: fit-content;
            font-weight: 600;
            border-radius: 5px;
            margin: 5px 5px 0;
        }
        .dates {
            display: flex;
        }
        .bottom {
            display: flex;
            justify-content: space-between;
            > p {
                margin: 5px 0;
                font-size: 0.75rem;
                font-weight: 600;
            }
            highlight {
                color: ${({ theme }): string => theme.colors.brandPrimary};
                border-right: 1px solid ${({ theme }): string => theme.colors.brandPrimary};
                ${({ theme: { right } }): string => `padding-${right}: 5px`};
            }
        }
    }
    .contentCtr {
        display: flex;
        padding: 15px;
        > img {
            margin: 0 15px;
        }
    }
`;

// ------------------------------------------------- TRIGGERS
const SubscriptionRow = styled.div`
    margin-top: 16px;
    // background-color: ${({ theme }): string => theme.colors.bgSecondary};
    border: 1px solid ${({ theme }): string => theme.colors.brandPrimary};
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    padding: 12px 16px;
    display: flex;
    align-items: center;

    .info {
        flex: 50%;

        p {
            font-weight: 600;
            font-size: 0.875rem;
        }

        button {
            margin-top: 3px;
            font-size: 0.8125rem;
            display: flex;
            align-items: center;
            color: ${({ theme }): string => theme.colors.bodyLight};

            > img {
                width: 14px;
                height: 14px;
                ${({ theme: { left } }): string => `margin-${left}: 5px`};
            }
        }
    }
`;

const SubscriptionChart = styled.div`
    // ${({ theme: { left } }): string => `margin-${left}: -6px`};
    ${({ theme: { colors, right } }): string => `border-${right}: 1px solid ${colors.border}`};
    ${({ theme: { right } }): string => `padding-${right}: 10px`};
    ${({ theme: { right } }): string => `margin-${right}: 15px`};
    display: none;

    @media ${breakpoints.minDesktop} {
        display: flex;
        flex: 25%;
        align-items: flex-end;
        justify-content: center;
    }

    .subscriptionStage {
        position: relative;

        span.value {
            position: absolute;
            bottom: 1px;
            left: 0;
            right: 0;
            text-align: center;
            font-weight: bold;
            font-size: 11px;
        }

        &:first-child {
            span.value {
                color: ${({ theme }): string => theme.colors.bodyAlt};
            }
        }
    }

    .arrow {
        width: 16px;
        height: 16px;
        ${({ theme: { right } }): string => `margin-${right}: 8px`};
    }
`;

const SubscriptionTrigger = styled.button`
    ${({ theme: { left } }): string => `margin-${left}: 12px`};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    border: 1px solid;
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
    padding: 0 6px;
    height: 44px; /* same as atc */
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${({ theme }): string => theme.colors.brandPrimary};
    transition: opacity ease-in-out 0.2s;

    img {
        width: 16px;
        height: 16px;
        margin: 1px 0 2px;
    }

    &:hover {
        opacity: 0.7;
    }
`;

const ManageTrigger = styled.button`
    ${({ theme: { left } }): string => `margin-${left}: 12px`};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    padding: 0 6px;
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${({ theme }): string => theme.colors.brandPrimary};
    transition: opacity ease-in-out 0.2s;

    &:hover {
        opacity: 0.7;
    }
`;

// ------------------------------------------------- MODAL
const Modal = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    z-index: 60;
    display: flex;
    align-items: center;
    justify-content: center;

    .scrim {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.5);
        backdrop-filter: blur(6px);
    }

    .content {
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
        overflow-y: auto;
        position: relative;
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    }
    .loaderContainer {
        min-height: 300px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const InfoContent = styled.div`
    padding: 32px 24px 40px;
    width: 40%;
    max-width: 480px;
    text-align: center;

    h3 {
        font-size: 1.25rem;
        font-weight: bold;
        margin-bottom: 16px;
    }

    img {
        max-width: 320px;
        margin: 0 auto 24px;
    }

    p.title {
        font-weight: 600;
        margin-bottom: 6px;
    }

    p.desc {
        margin-bottom: 16px;
        b {
            font-weight: 600;
        }
    }

    .actionRow {
        display: flex;
        align-items: center;
        justify-content: center;

        a,
        button {
            text-decoration: underline;

            &:hover {
                text-decoration: none;
            }
        }

        span {
            margin: 0 12px;
        }

        button {
            color: ${({ theme }): string => theme.colors.brandPrimary};
            text-decoration: underline;
        }
    }
`;

const SubscriptionContent = styled.div`
    width: 520px;

    .tabOptions {
        display: flex;

        button {
            flex: 1;
            padding: 20px 0;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 0.9375rem;
            border-bottom: 1px solid ${({ theme }): string => theme.colors.border};
            color: ${({ theme }): string => theme.colors.bodyLight};

            + button {
                ${({ theme: { colors, left } }): string => `border-${left}: 1px solid ${colors.border}`};
            }

            span.savingsTag {
                font-size: 0.6875rem;
                ${({ theme: { left } }): string => `margin-${left}: 8px`};
                background-color: ${({ theme }): string => theme.colors.border};
                color: ${({ theme }): string => theme.colors.bodyAlt};
                border-radius: 24px;
                padding: 3px 8px;
            }

            &:hover {
                color: ${({ theme }): string => theme.colors.brandPrimary};
            }

            &.active {
                color: ${({ theme }): string => theme.colors.brandPrimary};
                border-bottom-color: ${({ theme }): string => theme.colors.brandPrimary};
                box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.1);
                font-weight: 600;

                span.savingsTag {
                    background-color: #f68721;
                    font-weight: bold;
                }
            }
        }
    }
`;

const ScheduleSection = styled.div`
    padding: 24px 24px 32px;

    p.title {
        font-weight: 600;
        margin-bottom: 20px;
    }

    .scheduleOptions {
        display: flex;
        margin-left: -24px;
    }

    p.helper {
        margin-top: 24px;
        font-size: 0.75rem;
        color: ${({ theme }): string => theme.colors.bodyLight};
    }

    .subscribeEnticer {
        display: flex;
        background-color: #fffcdc;
        margin-top: 32px;
        padding: 14px 16px 16px;
        font-size: 0.875rem;
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};
        > img {
            margin-top: -30px;
        }

        p.title {
            font-weight: bold;
            color: ${({ theme }): string => theme.colors.body};
            margin-bottom: 2px;
            font-size: 0.9375rem;
        }

        p span {
            text-decoration: underline;
            cursor: pointer;
            font-weight: 600;

            &:hover {
                text-decoration: none;
            }
        }
    }

    .ctaContainer {
        margin-top: 24px;
    }
`;

const ScheduleOption = styled.div`
    flex: 1;
    ${({ theme: { left } }): string => `padding-${left}: 24px`};

    button.date {
        color: ${({ theme }): string => theme.colors.bodyLight};
        background-color: ${({ theme }): string => theme.colors.bodyAlt};
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};
        border: 1px solid ${({ theme }): string => theme.colors.border};
        font-size: 0.75rem;
        width: 100%;
        padding: 12px 0 8px;
        margin-bottom: 16px;
        font-weight: 600;

        strong {
            display: block;
            font-size: 1.125rem;
        }

        &.active {
            border-color: ${({ theme }): string => theme.colors.brandPrimary};
            color: ${({ theme }): string => theme.colors.body};
            box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1);

            strong {
                color: ${({ theme }): string => theme.colors.brandPrimary};
            }
        }

        &:hover {
            border-color: ${({ theme }): string => theme.colors.bodyMid};
        }
    }

    .qtyChanger {
        background-color: ${({ theme }): string => theme.colors.bgSecondary};
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 50% 0;

        button {
            position: relative;
            width: 100%;
            padding-bottom: 100%;
            border: 1px solid ${({ theme }): string => theme.colors.border};
            background-color: ${({ theme }): string => theme.colors.bgPrimary};
            border-radius: 100%;
            &.active {
                border-color: ${({ theme }): string => theme.colors.brandPrimary};
            }

            span {
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
            }

            img {
                transition: transform ease-in-out 0.2s;
            }

            &.addition {
                margin-top: -50%;
                border: 1px solid ${({ theme }): string => theme.colors.brandPrimary};

                &:not([disabled]):hover {
                    img {
                        transform: scale(1.2);
                    }
                }
            }

            &.removal {
                margin-bottom: -50%;

                &:not([disabled]):hover {
                    img {
                        transform: scale(0.8);
                    }
                }
            }

            &[disabled] {
                cursor: not-allowed;
            }
        }

        span.qty {
            padding: 10px 0;
            color: ${({ theme }): string => theme.colors.border};

            &.active {
                font-weight: bold;
                color: ${({ theme }): string => theme.colors.body};
            }
        }
    }
`;

const CalendarWeek = styled.div`
    display: flex;
    justify-content: space-between;
    flex: 50%;

    .dayContainer {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .label {
        font-size: 0.75rem;
        color: ${({ theme }): string => theme.colors.bodyLight};
        margin-bottom: 8px;
    }

    button {
        width: 24px;
        height: 24px;
        border-radius: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${({ theme }): string => theme.colors.bgSecondary};
        font-weight: 600;
        font-size: 0.75rem;
        position: relative;

        &:hover {
            background-color: ${({ theme }): string => theme.colors.brandPrimaryLight};
        }

        &.active {
            background-color: ${({ theme }): string => theme.colors.brandPrimary};
            color: ${({ theme }): string => theme.colors.bodyAlt};
        }

        .count {
            position: absolute;
            bottom: -7px;
            right: -7px;
            width: 16px;
            height: 16px;
            border-radius: 100%;
            background-color: ${({ theme }): string => theme.colors.brandSecondary};
            border: 2px solid;
            color: ${({ theme }): string => theme.colors.bodyAlt};
            font-size: 0.5rem;
            line-height: 1;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    // flex: 1 1 100%;
`;

export default Subscription;
